import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Editor } from "primereact/editor";

import { AuthContext } from "../../../../contexts/AuthContext";
import Loading from "../../components/Loading";
import CustomInput from "../../components/CustomInput";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import { bannerService } from "../../../../services/api/bannerService";
import { firebaseService } from "../../../../services/api/firebaseService";

/* ================= Utils ================= */
function fmtDateMaybe(val) {
  if (!val) return "--";
  if (typeof val === "string" && val.includes("/")) return val;
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString("vi-VN");
  } catch {
    return String(val);
  }
}

/* ================= Component ================= */
const BannerDetail = () => {
  const { id } = useParams();
  const { token } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const [data, setData] = useState({
    name: "",
    txtUrl: "",
    thumbnail: "",
    createdBy: "",
    createdAt: "",
  });

  const [fileHtml, setFileHtml] = useState("");

  const [editForm, setEditForm] = useState({
    name: "",
    content: "",
    imageFile: null,
    imagePreview: "",
  });

  /* ================= Fetch detail ================= */
  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res = await bannerService.get_detail(token, id);
      const payload = res?.data?.data || res?.data || {};

      setData({
        name: payload.name || "",
        txtUrl: payload.txtUrl || "",
        thumbnail: payload.thumbnail || "",
        createdBy: payload.createdBy || "",
        createdAt: payload.createdAt || "",
      });

      setFileHtml(payload.txtUrl || "");

      setEditForm({
        name: payload.name || "",
        content: payload.txtUrl || "",
        imageFile: null,
        imagePreview: payload.thumbnail || "",
      });
    } catch {
      toast.error("Không thể tải chi tiết banner");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  /* ================= Actions ================= */
  const handleSave = async () => {
    try {
      setSaving(true);

      let thumbnailUrl = data.thumbnail;

      if (editForm.imageFile instanceof File) {
        const upload = await firebaseService.upload_file(
          token,
          editForm.imageFile,
        );
        if (!upload?.url) throw new Error("Upload thumbnail thất bại");

        thumbnailUrl = upload.url;

        if (
          data.thumbnail &&
          data.thumbnail.startsWith("https://storage.googleapis.com/")
        ) {
          firebaseService.delete_file(token, data.thumbnail).catch(() => {});
        }
      }

      const fd = new FormData();
      fd.append("name", editForm.name.trim());
      fd.append("txtUrl", editForm.content);
      fd.append("thumbnail", thumbnailUrl);

      await bannerService.update(token, id, fd);

      setData((s) => ({
        ...s,
        name: editForm.name,
        txtUrl: editForm.content,
        thumbnail: thumbnailUrl,
      }));

      setFileHtml(editForm.content);
      setIsEditMode(false);

      toast.success("Cập nhật banner thành công");
    } catch (err) {
      toast.error(err.message || "Không thể lưu banner");
    } finally {
      setSaving(false);
    }
  };

  const confirmCancelEdit = () => {
    setEditForm({
      name: data.name,
      content: data.txtUrl,
      imageFile: null,
      imagePreview: data.thumbnail,
    });
    setIsEditMode(false);
    setShowCancelConfirm(false);
  };

  /* ================= Render ================= */
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg border h-full overflow-hidden">
        {/* ================= HEADER ================= */}
        <div className="border-b px-6 py-5 flex items-center justify-between">
          <h1 className="font-sf-semibold text-[#0457C2] text-2xl uppercase">
            {data.name || "—"}
          </h1>

          {!isEditMode ? (
            <button
              onClick={() => setIsEditMode(true)}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Chỉnh sửa
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => setShowCancelConfirm(true)}
                disabled={saving}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
              >
                Hủy bỏ
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? "Đang lưu..." : "Lưu lại"}
              </button>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex items-center divide-x-2 gap-4 px-6 pt-6">
          <div className="flex items-center gap-4">
            <span className="text-gray-500 text-xs">Người tạo:</span>
            <span className="font-medium text-xs">{data.createdBy || "—"}</span>
          </div>

          <div className="flex items-center gap-4 pl-4">
            <span className="text-gray-500 text-xs">Thời gian tạo:</span>
            <span className="font-medium text-xs">
              {fmtDateMaybe(data.createdAt)}
            </span>
          </div>
        </div>

        {/* ================= META ================= */}
        <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            className={`relative h-[32vh] rounded-xl overflow-hidden border ${
              isEditMode ? "cursor-pointer border-dashed" : ""
            }`}
            onClick={() => isEditMode && fileInputRef.current?.click()}
          >
            {editForm.imagePreview ? (
              <img
                src={editForm.imagePreview}
                className="w-full h-full object-cover"
                alt="thumbnail"
              />
            ) : (
              <div className="w-full h-full grid place-items-center text-gray-400">
                Không có hình ảnh
              </div>
            )}

            {isEditMode && (
              <div className="absolute inset-0 bg-black/10 opacity-0 hover:opacity-100 transition grid place-items-center">
                <div className="bg-white px-4 py-2 rounded-lg text-sm shadow">
                  Thay đổi ảnh
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept="image/*"
              onChange={(e) =>
                setEditForm((s) => ({
                  ...s,
                  imageFile: e.target.files?.[0] || null,
                  imagePreview: e.target.files?.[0]
                    ? URL.createObjectURL(e.target.files[0])
                    : s.imagePreview,
                }))
              }
            />
          </div>
        </div>

        {/* ================= CONTENT ================= */}
        <div className="px-6 pb-8">
          <div className="flex flex-col mb-4">
            <span className="text-gray-500 text-sm">Tiêu đề</span>
            {isEditMode ? (
              <CustomInput
                value={editForm.name}
                onChange={(v) => setEditForm((s) => ({ ...s, name: v }))}
              />
            ) : (
              <span className="font-semibold text-lg">{data.name}</span>
            )}
          </div>

          <div>
            <span className="text-gray-500 text-sm">Nội dung</span>
            {isEditMode ? (
              <Editor
                value={editForm.content}
                onTextChange={(e) =>
                  setEditForm((s) => ({ ...s, content: e.htmlValue }))
                }
                style={{ height: "45vh" }}
              />
            ) : fileHtml ? (
              <div
                className="prose max-w-none prose-img:max-w-full prose-img:rounded-lg"
                dangerouslySetInnerHTML={{ __html: fileHtml }}
              />
            ) : (
              <div className="text-gray-500 italic">Không có nội dung.</div>
            )}
          </div>
        </div>
      </div>

      {/* ================= CONFIRM CANCEL ================= */}
      <ConfirmDeleteModal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={confirmCancelEdit}
        title="Xác nhận hủy chỉnh sửa"
        message="Những thay đổi của bạn sẽ không được lưu. Bạn có chắc chắn muốn hủy?"
      />
    </>
  );
};

export default BannerDetail;
